<template>
  <h1>Profile</h1>
  <template v-if="signedIn">
    <div class="content">
      <div>
        <h3>Team</h3>
        <template v-if="!$store.state.team">
          <h5>Create Team</h5>
          <RemoteForm
            :form="createForm || $store.state.forms.create_team"
            button-text="Create team"
            @submit="createTeam"
          />
          <h5>Join Team</h5>
          <RemoteForm
            :form="joinForm || $store.state.forms.join_team"
            button-text="Join Team"
            @submit="joinTeam"
          />
        </template>
        <template v-else>
          <h4>{{ $store.state.team.name }}</h4>
          <h5 class="eligible">
            {{ $store.state.team.eligibility_class }}
          </h5>
          <RemoteForm
            :form="editForm || $store.state.forms.edit_team"
            button-text="Edit team"
            @submit="editTeam"
          />
          <h5>Join token</h5>
          <div class="token-container">
            <div>Hover to show token</div>
            <code>{{ $store.state.team.join_token }}</code>
          </div>
          <p>Give this out to people who should join your team.</p>
        </template>
      </div>
      <div>
        <h3>Link account</h3>
        <div class="sso-area">
          <div class="sso-container">
            <template v-for="provider in $store.state.oauthProviders" :key="provider">
              <OauthButton v-if="provider" :provider="provider" />
            </template>
          </div>
        </div>
      </div>
    </div>
    <div class="center">
      <button class="button signout-button" @click="signOut">
        Sign Out
      </button>
    </div>
    <div v-if="aiToken" class="center ai-token-wrap">
      <div class="ai-token-card">
        <div class="ai-token-header">
          <h5>AI token</h5>
          <button class="button ai-token-copy" @click="copyAiToken">
            Copy
          </button>
        </div>
        <p class="ai-token-help">
          Chat website:
          <a
            href="https://ssm-1984-chat.lovable.app"
            target="_blank"
            rel="noopener noreferrer"
          >ssm-1984-chat.lovable.app</a>
        </p>
        <code class="ai-token-value">{{ aiToken }}</code>
      </div>
    </div>
  </template>
  <div v-else class="center">
    <h3>Not logged in</h3>
  </div>
</template>

<script>
import OauthButton from '@/components/OauthButton.vue'
import RemoteForm from '@/components/RemoteForm.vue'
import networking from '@/networking.js'

export default {
    components: {
        OauthButton,
        RemoteForm,
    },
    data () {
        return {
            createForm: null,
            joinForm: null,
            editForm: null,
        }
    },
    computed: {
        signedIn () {
            return !!this.$store.state.user
        },
        aiToken () {
            return this.$store.state.user?.ai_token || null
        },
    },
    methods: {
        async createTeam (event) {
            const r = await networking.request('POST', '/api/team', event)
            if (r.status !== 200) {
                this.$store.commit('createToast', {
                    type: 'error',
                    title: `Backend could not be reached: ${r.status}`,
                    body: 'Please try again.',
                })
                return
            }
            const data = await r.json()
            this.createForm = data.form
            if (data.success) {
                this.$store.commit('updateUser', data.user)
                this.$store.commit('updateUserTeam', data.team)
                this.$store.commit('createToast', {
                    type: 'success',
                    title: 'Team successfully created!',
                })
            }
        },
        async joinTeam (event) {
            const r = await networking.request('POST', '/api/team/join', event)
            if (r.status !== 200) {
                this.$store.commit('createToast', {
                    type: 'error',
                    title: `Backend could not be reached: ${r.status}`,
                    body: 'Please try again.',
                })
                return
            }
            const data = await r.json()
            this.joinForm = data.form
            if (data.success) {
                this.$store.commit('updateUser', data.user)
                this.$store.commit('updateUserTeam', data.team)
                for (const challenge in data.challenges) {
                    this.$store.commit('processChallenge', data.challenges[challenge])
                }
                this.$store.commit('createToast', {
                    type: 'success',
                    title: `Successfully joined team ${data.team.name}!`,
                })
            }
        },
        async editTeam (event) {
            const r = await networking.request('PATCH', '/api/team', event)
            if (r.status !== 200) {
                this.$store.commit('createToast', {
                    type: 'error',
                    title: `Backend could not be reached: ${r.status}`,
                    body: 'Please try again.',
                })
                return
            }
            const data = await r.json()
            this.editForm = data.form
            if (data.success) {
                this.$store.commit('updateUserTeam', data.team)
                this.$store.commit('createToast', {
                    type: 'success',
                    title: 'Team successfully updated!',
                })
            }
        },
        async copyAiToken () {
            await navigator.clipboard.writeText(this.aiToken)
            this.$store.commit('createToast', {
                type: 'success',
                title: 'AI token copied',
            })
        },
        async signOut () {
            const r = await networking.request('POST', '/api/logout')
            if (r.status !== 200) {
                this.$store.commit('createToast', {
                    type: 'error',
                    title: `Backend could not be reached: ${r.status}`,
                    body: 'Please try again.',
                })
                return
            }
            const data = await r.json()
            if (data.success) {
                this.$store.commit('logout')
                this.$router.replace({ name: 'home' })
                this.$store.commit('createToast', {
                    type: 'success',
                    title: 'Signed out',
                })
            }
        },
    },
}
</script>

<style lang="scss" scoped>
* {
    text-align: center;
}

p {
    margin: 0;
}

.eligible {
    color: $color-white;
}

.ai-token-wrap {
    margin-top: 2rem;
}

.ai-token-card {
    margin: 0 auto;
    padding: 1rem;
    max-width: 34rem;
    border: 1px solid rgba($color-accent, 0.45);
    border-radius: $standard-border-radius;
    background-color: rgba($challenge-color, 0.32);
    box-shadow: 0 0 0 1px rgba($challenge-color, 0.2);
}

.ai-token-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;

    h5 {
        margin: 0;
    }
}

.ai-token-copy {
    margin: 0;
    background-color: $color-accent;
    color: $color-black;
}

.ai-token-help {
    margin: 0.75rem 0;
    color: $color-white-soft;
}

.ai-token-value {
    display: block;
    padding: 0.875rem 1rem;
    text-align: left;
    word-break: break-all;
    white-space: pre-wrap;
    border-radius: $standard-border-radius;
    background-color: rgba($color-body-background, 0.55);
    border: 1px solid rgba($color-accent, 0.22);
}

.token-container {
    position: relative;
    display: inline-block;

    div {
        position: absolute;
        left: 0;
        width: 100%;
        text-align: center;
        background-color: $color-black;
        border-radius: $standard-border-radius;
    }

    &:hover {
        div {
            display: none;
        }
    }
}

.content {
    display: grid;
    grid-template-columns: 50% 50%;
    padding-top: 3rem;
}

@media only screen and (max-width: 990px) {
    .content {
        display: block;
    }

    .ai-token-header {
        flex-direction: column;
    }
}

.sso-area {
  text-align: center;
}

.sso-container {
  display: inline-block;
}

.signout-button {
    margin-top: 1rem;
}
</style>
